import { useUserEventLogger } from '@repo/tracking-service';
import { Description } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { kebabCase } from 'es-toolkit';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { BusinessPlaceType, businessPlaceTypeQuery } from '@/__generated__/businessPlaceTypeQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { AnswerList, CTA, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertBusinessPlaceType } from '@/graphql/mutation/survey/business';
import { officeTypeQuery } from '@/graphql/query/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';

const answers: { value: BusinessPlaceType; label: string }[] = [
  { value: 'lease', label: '임대했어요' },
  { value: 'own', label: '자가로 구매했어요' },
  { value: 'noHave', label: '사업장이 따로 없어요' },
  { value: 'etc', label: '기타' }
];

export const BusinessOfficeTypeContainer = ({ queryRef }: { queryRef: PreloadedQuery<businessPlaceTypeQuery> }) => {
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const router = useRouter();
  const { getSurveyBusinessAnswer } = usePreloadedQuery<businessPlaceTypeQuery>(officeTypeQuery, queryRef);
  const [upsertAnswer, isLoading] = useUpsertBusinessAnswer(upsertBusinessPlaceType);

  const [selectedAnswer, setSelectedAnswer] = useState<BusinessPlaceType | undefined>();
  const [comment, setComment] = useState('');

  const prevAnswer = getSurveyBusinessAnswer?.businessAnswer?.[0]?.businessPlaceType;
  const prevComment = getSurveyBusinessAnswer?.businessAnswer?.[0]?.businessPlaceTypeComment;
  useEffect(() => {
    if (prevAnswer) {
      setSelectedAnswer(prevAnswer);
    }
    if (prevComment) {
      setComment(prevComment);
    }
  }, [prevAnswer, prevComment]);

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_office_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <ProgressBar step={4} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_office_confirm', eventAttributes);

            upsertAnswer(
              {
                businessPlaceType: selectedAnswer,
                businessPlaceTypeComment: selectedAnswer === 'etc' ? comment : ''
              },
              {
                onCompleted: () => router.push(SURVEY_PAGES.주소이전_현재주소_질문)
              }
            );
          }}
        >
          <article>
            <section>
              <h1 className={common.content_title}>
                어떤 형태로
                <br />
                사업장을 구하셨나요?
              </h1>

              <AnswerList
                answers={answers}
                checkedValue={selectedAnswer}
                onChange={(value) => {
                  sendClickEvent(`survey_office_${kebabCase(value)}`, eventAttributes);

                  setSelectedAnswer(value);
                }}
              />
            </section>
            <section className={'pt-3'}>
              <Description.Feedback
                hasLinkIcon={false}
                hasBg={false}
                hasStatusIcon
                status={'info'}
                text={
                  '집 주소로 사업장을 등록한 경우를 포함하여 온라인 운영이나 운수업, 청소업체나 시공 등으로 사업장 없이 사업을 운영한 경우 [사업장이 따로 없어요] 를 선택해주세요.'
                }
              />
              {selectedAnswer === 'etc' && (
                <div className={'pb-3'}>
                  <p className={common.section_title}>기타로 선택하신 내용을 입력해주세요</p>
                  <textarea className={common.etc_textarea} value={comment} onChange={(e) => setComment(e.target.value)} />
                </div>
              )}
            </section>
            <section className={'pt-6'}>
              <h2 className={common.section_title}>사업장 형태, 왜 확인이 필요한가요?</h2>
              <p className={common.section_list_item}>사업장 형태에 따라 더 유리한 세액 감면 혜택을 비교해드리기 위해서 필요해요.</p>
            </section>
          </article>

          {selectedAnswer && (
            <CTA type={'submit'} disabled={!selectedAnswer || (selectedAnswer === 'etc' && !comment) || isLoading}>
              {selectedAnswer !== 'etc' ? '다음' : selectedAnswer === 'etc' && !comment ? '기타 내용을 입력해주세요' : '입력 완료'}
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};
